import FilterSection from "@/components/Marketplace/FilterSection";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import ArtistCard from "@/components/ui/ArtistCard";
import { LoadMoreButton } from "@/components/ui/buttons/LoadMoreButton";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { hasActiveSession } from "@/lib/auth";
import { useFollowedCreatorsInfiniteList } from "@/lib/lutely-api/hooks";
import { saveRedirect } from "@/lib/storage-utils";
import { Flex, Text, TextField } from "@radix-ui/themes";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { noindexHead } from "@/lib/seo";

export const Route = createFileRoute("/account/followed-artists")({
  component: FollowedArtists,
  head: () => noindexHead("Followed Artists | Lutely"),
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await hasActiveSession();
    if (!isAuthenticated) {
      saveRedirect(location.href);
      throw redirect({
        to: "/",
        search: { auth: "login" as const },
      });
    }
  },
});

function FollowedArtists() {
  const { data: user } = useLoggedInUser();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useFollowedCreatorsInfiniteList(
      { search: debouncedSearch || undefined },
      { enabled: !!user },
    );

  const creators = data?.pages.flatMap((page) => page.content ?? []) ?? [];

  return (
    <ContainerWrapper>
      <Flex className="pt-[25px]">
        <FilterSection />
        <Flex direction="column" className="pl-0 lg:pl-[50px] flex-1 min-w-0">
          <Text className="font-bold text-[25px]">Followed Artists</Text>

          <TextField.Root
            placeholder="Search followed artists..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-[20px] w-full sm:w-[280px]"
          />

          <Flex direction="column" className="w-full mt-[20px]">
            {!isLoading && creators.length === 0 ? (
              <Text className="text-text-secondary text-[14px] py-8 text-center">
                {debouncedSearch
                  ? "No followed artists match your search."
                  : "You aren't following any artists yet — hit Follow on an artist page to see them here."}
              </Text>
            ) : (
              <Flex className="flex-wrap gap-[24px]">
                {creators.map((creator) => (
                  <ArtistCard key={creator.creatorId} creator={creator} />
                ))}
              </Flex>
            )}
            {creators.length > 0 && (
              <LoadMoreButton
                isLoading={isFetchingNextPage}
                hasMorePages={!!hasNextPage}
                showNoMoreItems={false}
                onLoadMore={() => fetchNextPage()}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </ContainerWrapper>
  );
}
